import { useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Button, Icon, Input } from '@aidkitorg/component-library';
import type { Dashboard, DashboardComponent, Survey, Text } from '@aidkitorg/types/lib/survey';
import type { SupportedDashboardComponent } from '@aidkitorg/types/lib/admin-panel/dashboard';
import { encodeWaypoint } from '@aidkitorg/types/lib/translation/componentIdentifier';
import { adaptDashboardComponents, type AdaptedDashboardComponent } from '../adapters/distroToAdminPanel';
import { DashboardComponentCard } from './DashboardComponents/DashboardComponentCard';
import { DeferredDataCard } from './UniversalDashboardGridItem';
import { DashboardsProvider } from '../../../Contexts/DashboardsContext';
import { ReportsProvider } from '../../../Contexts/ReportsContext';
import { SettingsProvider } from '../../../Contexts/SettingsContext';
import { useLocalizedStrings } from '../../../Localization';
import { findAndGoToComponent, getChildComponents } from '../../../ConfigSearch';
import type { SearchableComponent } from '../../../Config/Util';

const PREVIEW_WIDTH = 800;
const PREVIEW_HEIGHT = 400;

type DashboardEntry = {
  dashboard: Dashboard,
  /** Waypoint chain (see `encodeWaypoint`) locating this Dashboard for `findAndGoToComponent`. */
  tree: string[],
};

// Walks with `getChildComponents` using the same traversal + encodeWaypoint strategy
// that ConfigSearch's and the Distro Navigation Preview use
function collectDashboards(components: SearchableComponent[]): DashboardEntry[] {
  const found: DashboardEntry[] = [];
  const walk = (comps: SearchableComponent[], parentTree: string[]) => {
    comps.forEach((component, i) => {
      const tree = [...parentTree, encodeWaypoint(component, i)];
      if (component.kind === 'Dashboard') {
        found.push({ dashboard: component as unknown as Dashboard, tree });
        return;
      }
      const children = getChildComponents(component);
      if (children) walk(children, tree);
    });
  };
  walk(components, []);
  return found;
}

function resolveText(value: Text | string | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.en || Object.values(value)[0] || '';
}

function dashboardDescription(d: Dashboard): string {
  return resolveText((d as { description?: Text | string }).description);
}

// Dashboard Sections aren't siblings in the adapter output; their widgets live
// under `children`. Unwrap them recursively so only leaf widgets remain.
function flattenLeafComponents(adapted: AdaptedDashboardComponent[]): AdaptedDashboardComponent[] {
  const leaves: AdaptedDashboardComponent[] = [];
  for (const entry of adapted) {
    if ((entry.original as any)?.kind === 'Dashboard Section') {
      if (entry.children?.length) leaves.push(...flattenLeafComponents(entry.children));
    } else {
      leaves.push(entry);
    }
  }
  return leaves;
}

function componentTitle(component: DashboardComponent): string {
  const c = component as any;
  const raw = c?.title;
  const title = resolveText(raw);
  const kind = 'kind' in component ? component.kind : 'Component';
  return title || kind;
}

// The adapter leaves these as `adapted: null` (they render from precomputed
// /load results), but DeferredDataCard can still preview them.
const DEFERRED_PREVIEW_KINDS = new Set(['Custom Query', 'Hero Count', 'Applicant Table']);

// Mirror UniversalDashboardGridItem's effectiveDashboardFilter: a leaf unwrapped
// from a Dashboard Section inherits that section's filter. The live dashboard
// defaults to the first section tab, so preview with index 0 to match it.
function effectivePreviewFilter(
  adapted: AdaptedDashboardComponent,
  dashboardFilter: any,
): any {
  const sectionFilter = adapted.parentSectionFilters?.[0]?.filter;
  if (!sectionFilter) return dashboardFilter;
  if (dashboardFilter) return { kind: 'And' as const, clauses: [dashboardFilter, sectionFilter] };
  return sectionFilter;
}

export function ConfigDashboardsPreview({ components }: { components: Survey }) {
  // The Config editor route lives outside AdminPanelApp, which owns the providers
  // the dashboard renderer depends on. Provide them here or the widgets fail
  // before any /dashboard/load request fires.
  const queryClient = useMemo(() => new QueryClient(), []);
  return (
    <QueryClientProvider client={queryClient}>
      <ReportsProvider>
        <DashboardsProvider>
          <SettingsProvider>
            <ConfigDashboardsPreviewInner components={components} />
          </SettingsProvider>
        </DashboardsProvider>
      </ReportsProvider>
    </QueryClientProvider>
  );
}

function ConfigDashboardsPreviewInner({ components }: { components: Survey }) {
  const L = useLocalizedStrings();
  const [search, setSearch] = useState('');
  const [componentSearch, setComponentSearch] = useState('');
  // Select by index, not path: unpublished dashboards can share a blank path.
  const [selectedDashboardIdx, setSelectedDashboardIdx] = useState<number | null>(null);
  const [selectedComponentIdx, setSelectedComponentIdx] = useState<number | null>(null);

  const dashboards = useMemo(() => collectDashboards(components as SearchableComponent[]), [components]);

  const selectedEntry = selectedDashboardIdx != null ? dashboards[selectedDashboardIdx] ?? null : null;
  const selectedDashboard = selectedEntry?.dashboard ?? null;

  const adaptedComponents = useMemo(
    () => (selectedDashboard
      ? flattenLeafComponents(adaptDashboardComponents(selectedDashboard.components))
      : []),
    [selectedDashboard],
  );

  const selectedAdapted = selectedComponentIdx != null ? adaptedComponents[selectedComponentIdx] : undefined;

  const previewFilter = selectedAdapted && selectedDashboard
    ? effectivePreviewFilter(selectedAdapted, selectedDashboard.filter)
    : undefined;

  // Key on the serialized definition + filter, not just the index, so editing the
  // widget in Distro remounts the card and re-runs its load ("updated as you build").
  const previewKey = useMemo(
    () => (selectedAdapted
      ? `${selectedComponentIdx}:${JSON.stringify(selectedAdapted.adapted ?? selectedAdapted.original)}:${JSON.stringify(previewFilter)}`
      : `${selectedComponentIdx}`),
    [selectedComponentIdx, selectedAdapted, previewFilter],
  );

  const filteredDashboards = useMemo(() => {
    const withIdx = dashboards.map((entry, idx) => ({ entry, idx }));
    const q = search.trim().toLowerCase();
    if (!q) return withIdx;
    return withIdx.filter(({ entry }) => {
      const title = resolveText(entry.dashboard.title).toLowerCase();
      const description = dashboardDescription(entry.dashboard).toLowerCase();
      return title.includes(q) || description.includes(q) || (entry.dashboard.path ?? '').toLowerCase().includes(q);
    });
  }, [dashboards, search]);

  const filteredComponents = useMemo(() => {
    const withIdx = adaptedComponents.map((adapted, idx) => ({ adapted, idx }));
    const q = componentSearch.trim().toLowerCase();
    if (!q) return withIdx;
    return withIdx.filter(({ adapted }) => componentTitle(adapted.original).toLowerCase().includes(q));
  }, [adaptedComponents, componentSearch]);

  if (!dashboards.length) {
    return <div className="mt-4 ml-1">{L.admin_panel.dashboards.dashboards_empty_msg}</div>;
  }

  let body: React.ReactNode;
  if (!selectedDashboard) {
    body = (
      <>
        <Input
          label={L.admin_panel.dashboards.search_dashboards}
          showLabel={false}
          placeholder={L.admin_panel.dashboards.search_dashboards}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <ol className="list-decimal pl-2 marker:text-gray-400">
          {filteredDashboards.map(({ entry, idx }, position) => (
            <div className='flex'>
              <button
                type="button"
                className="rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 p-1 mr-3 my-1"
                title="Scroll to this Dashboard's definition in the editor"
                onClick={() => findAndGoToComponent(entry.tree, components)}
              >
                <Icon name="MapPinIcon" type="solid" role="decorative" size="sm" />
              </button>
              <li key={idx} className="text-sm text-gray-400 items-center gap-1.5 ml-3">
                <button
                  className="text-left py-1.5 text-gray-700 hover:underline focus:underline focus:outline-none"
                  onClick={() => {
                    setSelectedDashboardIdx(idx);
                    setSelectedComponentIdx(null);
                    setComponentSearch('');
                  }}
                >
                  <span className="font-medium">{resolveText(entry.dashboard.title) || entry.dashboard.path || `#${position + 1}`}</span>
                  {dashboardDescription(entry.dashboard) && (
                    <span className="text-gray-500"> — {dashboardDescription(entry.dashboard)}</span>
                  )}
                </button>
              </li>
            </div>
          ))}
        </ol>
      </>
    );
  } else if (selectedAdapted) {
    body = (
      <>
        <div className="flex items-center gap-0.5">
          <Button
            variant="link"
            onClick={() => setSelectedComponentIdx(null)}
            icon={<Icon name="ChevronLeftIcon" type="solid" role="decorative" />}
          />
          <span className="text-sm font-medium">{componentTitle(selectedAdapted.original)}</span>
        </div>

        <div className="pt-1">
          {selectedAdapted.adapted ? (
            <DashboardComponentCard
              key={previewKey}
              id={`config-preview-${selectedComponentIdx}`}
              component={selectedAdapted.adapted as SupportedDashboardComponent}
              dashboardFilter={previewFilter}
              width={PREVIEW_WIDTH}
              height={PREVIEW_HEIGHT}
            />
          ) : DEFERRED_PREVIEW_KINDS.has((selectedAdapted.original as any)?.kind) ? (
            <DeferredDataCard
              key={previewKey}
              original={selectedAdapted.original}
              componentId={`config-preview-${selectedComponentIdx}`}
              dashboardFilter={previewFilter}
              download={selectedAdapted.download}
              width={PREVIEW_WIDTH}
              height={PREVIEW_HEIGHT}
            />
          ) : (
            <div className="text-sm text-gray-500">{L.admin_panel.dashboards.not_previewable}</div>
          )}
        </div>
      </>
    );
  } else {
    body = (
      <>
        <div className="flex items-center gap-0.5">
          <Button
            variant="link"
            onClick={() => {
              setSelectedDashboardIdx(null);
              setSelectedComponentIdx(null);
            }}
            icon={<Icon name="ChevronLeftIcon" type="solid" role="decorative" />}
          />
          <span className="text-sm font-medium">{resolveText(selectedDashboard.title) || selectedDashboard.path}</span>
        </div>

        {adaptedComponents.length === 0 ? (
          <div className="px-1 py-1.5 text-sm text-gray-500">{L.admin_panel.dashboards.empty_dashboard}</div>
        ) : (
          <>
            <Input
              label={L.admin_panel.dashboards.search_components}
              showLabel={false}
              placeholder={L.admin_panel.dashboards.search_components}
              value={componentSearch}
              onChange={(e) => setComponentSearch(e.target.value)}
            />
            <ol className="list-decimal pl-6 marker:text-gray-400">
              {filteredComponents.map(({ adapted, idx }) => (
                <li key={idx} className="text-sm text-gray-400">
                  <button
                    className="text-left py-1.5 text-gray-700 hover:underline focus:underline focus:outline-none"
                    onClick={() => setSelectedComponentIdx(idx)}
                  >
                    {componentTitle(adapted.original)}
                  </button>
                </li>
              ))}
            </ol>
          </>
        )}
      </>
    );
  }

  return (
    <div className="mt-1 space-y-2 pl-2">
      <p className="text-sm text-gray-500">{L.admin_panel.dashboards.preview_note}</p>
      {body}
    </div>
  );
}
